/**
 * Grupos de elección tipo "Arma tu pizza": N opciones gratis, resto con precio por ítem.
 * Campo en producto: choice_groups[]
 */

/** @param {unknown} product */
export function getProductChoiceGroups(product) {
  const raw = product?.choice_groups ?? product?.choiceGroups;
  if (!Array.isArray(raw)) return [];
  return raw.map(normalizeChoiceGroup).filter((g) => g.options.length > 0);
}

/** @param {unknown} group */
export function normalizeChoiceGroup(group) {
  const maxRaw = group?.max_selections ?? group?.maxSelections;
  const maxParsed = maxRaw == null || maxRaw === '' ? null : parseInt(maxRaw, 10);
  return {
    name: String(group?.name ?? 'Ingredientes').trim() || 'Ingredientes',
    free_included: Math.max(0, parseInt(group?.free_included ?? group?.freeIncluded ?? 0, 10) || 0),
    max_selections: maxParsed != null && !Number.isNaN(maxParsed) && maxParsed > 0 ? maxParsed : null,
    options: (Array.isArray(group?.options) ? group.options : [])
      .map((o) => ({
        name: String(o?.name ?? '').trim(),
        price: Math.max(0, Number(o?.price) || 0),
      }))
      .filter((o) => o.name),
  };
}

/** @param {unknown} groups */
export function normalizeChoiceGroupsForSave(groups) {
  if (!Array.isArray(groups)) return [];
  return groups.map(normalizeChoiceGroup).filter((g) => g.options.length > 0);
}

/** @param {unknown} product @param {string} optionName */
export function isChoiceGroupOption(product, optionName) {
  const key = String(optionName ?? '').trim().toLowerCase();
  if (!key) return false;
  return getProductChoiceGroups(product).some((g) =>
    g.options.some((o) => o.name.toLowerCase() === key),
  );
}

/** @param {Record<string, string[]>} selectedChoices @param {object} group @param {string} optionName */
export function toggleChoiceSelection(selectedChoices, group, optionName) {
  const gName = group.name;
  const current = selectedChoices[gName] || [];
  const idx = current.indexOf(optionName);
  if (idx >= 0) {
    return { ...selectedChoices, [gName]: current.filter((_, i) => i !== idx) };
  }
  const max = group.max_selections;
  if (max != null && current.length >= max) {
    return selectedChoices;
  }
  return { ...selectedChoices, [gName]: [...current, optionName] };
}

/** @param {unknown} product @param {Record<string, string[]>} selectedChoices */
export function computeChoiceSelectionsAddOns(product, selectedChoices) {
  const groups = getProductChoiceGroups(product);
  const addOns = [];
  for (const group of groups) {
    const selected = selectedChoices[group.name] || [];
    selected.forEach((optName, idx) => {
      const match = group.options.find((o) => o.name === optName);
      if (!match) return;
      const price = idx < group.free_included ? 0 : match.price;
      addOns.push({ name: match.name, price, quantity: 1 });
    });
  }
  return addOns;
}

/** @param {unknown} product @param {Record<string, string[]>} selectedChoices */
export function computeChoiceExtraTotal(product, selectedChoices) {
  return computeChoiceSelectionsAddOns(product, selectedChoices).reduce(
    (sum, a) => sum + (Number(a.price) || 0) * (Number(a.quantity) || 1),
    0,
  );
}

/**
 * @param {unknown} product
 * @param {Record<string, string[]>} selectedChoices
 * @param {object[]} regularAddOns
 */
export function mergeAddOnsWithChoices(product, selectedChoices, regularAddOns) {
  const choiceAddOns = computeChoiceSelectionsAddOns(product, selectedChoices);
  const choiceNames = new Set(choiceAddOns.map((a) => String(a.name).toLowerCase()));
  const filtered = (regularAddOns || []).filter(
    (a) => !choiceNames.has(String(a?.name ?? '').toLowerCase()),
  );
  return [...choiceAddOns, ...filtered];
}

/** @param {Record<string, string[]>} selectedChoices @param {string} groupName */
export function countSelectedInGroup(selectedChoices, groupName) {
  return (selectedChoices[groupName] || []).length;
}

/**
 * Precio que tendría una opción si el cliente la selecciona ahora.
 * @param {object} group
 * @param {object} option
 * @param {number} selectedCount
 */
export function previewChoiceOptionPrice(group, option, selectedCount) {
  if (selectedCount < group.free_included) return 0;
  return Math.max(0, Number(option.price) || 0);
}

/**
 * Etiqueta de precio para una opción ya seleccionada.
 * @param {object} group
 * @param {number} selectionIndex
 */
export function labelForSelectedChoice(group, selectionIndex) {
  if (selectionIndex < group.free_included) return 'Gratis';
  const opt = group.options[selectionIndex];
  return opt ? `+$${(Number(opt.price) || 0).toLocaleString()}` : '';
}

/** @param {unknown} product */
export function getRegularAddOns(product) {
  const addOns = Array.isArray(product?.add_ons) ? product.add_ons : [];
  return addOns.filter((a) => !isChoiceGroupOption(product, a?.name));
}

/** Grupo por defecto al activar "Arma tu producto". */
export function emptyChoiceGroup() {
  return {
    name: 'Ingredientes',
    free_included: 3,
    max_selections: null,
    options: [],
  };
}
